import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Card, Spinner, Toast, ToastContainer, Alert } from 'react-bootstrap';
import { UserDetailsForm, ImageUpload, ScanResults } from '../components';
import { KTSVG } from '../../../../_metronic/helpers/components/KTSVG';
import {
  PostureScanUserData,
  PostureScanImages,
  PostureScanAPIResponse,
  PostureScanStep,
} from '../types';
import { submitPostureScan, PostureScanAPIError, logPostureScanResults } from '../api/postureScanApi';
import { isPostureScanConfigured, getConfigErrorMessage, STORAGE_KEYS } from '../config';

const OpenUserPostureScanPage: React.FC = () => {
  const location = useLocation();
  
  // Extract crmclientid from URL query params (optional parameter)
  const crmClientId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get('crmclientid') || null;
  }, [location.search]);

  const [currentStep, setCurrentStep] = useState<PostureScanStep>(PostureScanStep.USER_INFO);
  const [userData, setUserData] = useState<PostureScanUserData | null>(null);
  const [images, setImages] = useState<PostureScanImages | null>(null);
  const [scanResults, setScanResults] = useState<PostureScanAPIResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  
  // AbortController for API request cancellation
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load saved data on mount
  useEffect(() => {
    const savedUserData = localStorage.getItem(STORAGE_KEYS.USER_DATA);
    if (savedUserData) {
      try {
        setUserData(JSON.parse(savedUserData));
      } catch (err) {
        console.error('Failed to load saved user data:', err);
      }
    }
  }, []);

  // Cleanup AbortController on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const showMessage = (message: string) => {
    setToastMessage(message);
    setShowToast(true);
  };

  const handleUserDataSubmit = (data: PostureScanUserData) => {
    setUserData(data);
    setCurrentStep(PostureScanStep.UPLOAD_IMAGES);
  };

  const handleBackToUserInfo = () => {
    setCurrentStep(PostureScanStep.USER_INFO);
    setImages(null);
    setError(null);
  };

  const handleImagesSubmit = async (imageData: PostureScanImages) => {
    console.log('=== IMAGES SUBMITTED ===');
    setImages(imageData);
    setCurrentStep(PostureScanStep.PROCESSING);
    setError(null);

    // Check configuration
    if (!isPostureScanConfigured()) {
      const configError = getConfigErrorMessage();
      console.error('Configuration error:', configError);
      setError(configError);
      setCurrentStep(PostureScanStep.ERROR);
      showMessage(configError);
      return;
    }

    // Create new AbortController for this request
    abortControllerRef.current = new AbortController();

    try {
      console.log('Submitting posture scan...');
      
      const result = await submitPostureScan(
        imageData.frontImage,
        imageData.sideImage,
        abortControllerRef.current.signal
      );

      console.log('=== SCAN SUCCESS ===');
      console.log('Result:', result);

      // Save results (not images - too large for localStorage)
      setScanResults(result);
      
      // Only save small result data, not the images
      try {
        localStorage.setItem(STORAGE_KEYS.RESULTS, JSON.stringify(result));
      } catch (storageError) {
        console.warn('Could not save results to localStorage:', storageError);
        // Continue anyway - results are in state
      }

      // Log results to webhook (non-blocking)
      if (userData) {
        logPostureScanResults(userData, result, crmClientId).catch(err => {
          console.warn('Webhook logging failed (non-critical):', err);
        });
      }

      // Show results
      setCurrentStep(PostureScanStep.RESULTS);
      showMessage('Posture scan completed successfully!');

    } catch (err: any) {
      console.error('=== SCAN ERROR ===');
      console.error('Error:', err);

      // Don't show error if request was aborted
      if (err.name === 'AbortError' || err.message?.includes('cancelled')) {
        console.log('Request was cancelled');
        return;
      }

      let errorMessage = 'Failed to process scan. Please try again.';

      if (err instanceof PostureScanAPIError) {
        errorMessage = err.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setCurrentStep(PostureScanStep.ERROR);
      showMessage(errorMessage);
    }
  };

  const handleBackToUpload = () => {
    setCurrentStep(PostureScanStep.UPLOAD_IMAGES);
    setScanResults(null);
    setError(null);
  };

  const handleNewScan = () => {
    // Clear all state
    setCurrentStep(PostureScanStep.USER_INFO);
    setUserData(null);
    setImages(null);
    setScanResults(null);
    setError(null);

    // Clear storage (images were never stored)
    localStorage.removeItem(STORAGE_KEYS.USER_DATA);
    localStorage.removeItem(STORAGE_KEYS.RESULTS);

    showMessage('Starting new scan...');
  };

  return (
    <div className="min-vh-100 bg-light py-5">
      <Container style={{ maxWidth: '900px' }}>
        {/* Step 1: User Information Form */}
        {currentStep === PostureScanStep.USER_INFO && (
          <UserDetailsForm onSubmit={handleUserDataSubmit} initialData={userData || undefined} />
        )}

        {/* Step 2: Upload Images */}
        {currentStep === PostureScanStep.UPLOAD_IMAGES && (
          <ImageUpload
            onContinue={handleImagesSubmit}
            onBack={handleBackToUserInfo}
            initialImages={images}
          />
        )}

        {/* Step 3: Processing */}
        {currentStep === PostureScanStep.PROCESSING && (
          <Card className="border-0 shadow-lg">
            <Card.Body className="text-center py-5">
              <div className="mb-4">
                <Spinner 
                  animation="border" 
                  variant="primary" 
                  style={{ width: '5rem', height: '5rem', borderWidth: '0.4rem' }} 
                />
              </div>
              <h2 className="mb-3 d-flex align-items-center justify-content-center">
                <span className="svg-icon svg-icon-2hx svg-icon-primary me-3">
                  <KTSVG path='/media/icons/duotune/technology/teh004.svg' className='svg-icon-2hx' />
                </span>
                Analyzing Your Posture
              </h2>
              <p className="text-muted fs-5 mb-4">
                Our AI is processing your images...
              </p>
              
              {/* Analysis Steps Animation */}
              <div className="mt-4">
                <div className="d-flex justify-content-center gap-3 flex-wrap">
                  <div className="badge bg-primary-subtle text-primary fs-6 p-3">
                    <KTSVG path='/media/icons/duotune/general/gen049.svg' className='svg-icon-3 me-2' />
                    Front View
                  </div>
                  <div className="badge bg-success-subtle text-success fs-6 p-3">
                    <KTSVG path='/media/icons/duotune/general/gen049.svg' className='svg-icon-3 me-2' />
                    Side View
                  </div>
                  <div className="badge bg-info-subtle text-info fs-6 p-3">
                    <KTSVG path='/media/icons/duotune/general/gen048.svg' className='svg-icon-3 me-2' />
                    Analysis
                  </div>
                  <div className="badge bg-warning-subtle text-warning fs-6 p-3">
                    <KTSVG path='/media/icons/duotune/general/gen043.svg' className='svg-icon-3 me-2' />
                    Recommendations
                  </div>
                </div>
              </div>
              
              <p className="text-muted mt-4 mb-0">
                <small>This may take a few moments...</small>
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Step 4: Results */}
        {currentStep === PostureScanStep.RESULTS && scanResults && (
          <ScanResults
            data={scanResults}
            userData={userData}
            onBackToUpload={handleBackToUpload}
            onNewScan={handleNewScan}
          />
        )}

        {/* Step 5: Error */}
        {currentStep === PostureScanStep.ERROR && (
          <Card className="border-0 shadow-lg border-danger">
            <Card.Body className="p-5">
              <div className="text-center mb-4">
                <KTSVG path="/media/icons/duotune/general/gen044.svg" className="svg-icon-5x text-danger mb-3" />
                <h3 className="text-danger mb-3">Scan Failed</h3>
              </div>
              
              <Alert variant="danger">
                <Alert.Heading className="h5">Error Details</Alert.Heading>
                <p className="mb-0">{error || 'An unexpected error occurred. Please try again.'}</p>
              </Alert>

              {!isPostureScanConfigured() && (
                <Alert variant="warning" className="mt-3">
                  <Alert.Heading className="h6">Configuration Required</Alert.Heading>
                  <p className="mb-2">To use the Posture Scan feature, you need to configure the API key:</p>
                  <ol className="mb-0 small">
                    <li>Add <code>REACT_APP_WYH_POSTURE_API_KEY</code> to your <code>.env</code> file</li>
                    <li>Restart the development server</li>
                    <li>Try the scan again</li>
                  </ol>
                </Alert>
              )}

              <div className="d-flex gap-3 justify-content-center mt-4">
                <button
                  onClick={handleBackToUpload}
                  className="btn btn-outline-primary btn-lg"
                >
                  Back to Upload
                </button>
                <button
                  onClick={handleNewScan}
                  className="btn btn-primary btn-lg"
                >
                  Start New Scan
                </button>
              </div>
            </Card.Body>
          </Card>
        )}
      </Container>

      <ToastContainer position="bottom-center" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
          bg="dark"
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default OpenUserPostureScanPage;
